import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { StaggerContainer, StaggerItem } from '@/components/ui/motion';

interface SuccessStory {
  id: number;
  petName: string;
  ownerName: string;
  location: string;
  adoptionDate: string;
  story: string;
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
}

interface SuccessStoriesSectionProps {
  stories: SuccessStory[];
}

const SuccessStoriesSection = ({ stories }: SuccessStoriesSectionProps) => {
  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-secondary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full">
            <Icon name="SparklesIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-medium text-accent">Transformation Stories</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-semibold text-foreground">
            Love Changes Everything
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real stories of hope, healing, and the incredible bond between pets and their families.
          </p>
        </div>

        <StaggerContainer className="grid lg:grid-cols-2 gap-8">
          {stories.map((story) => (
            <StaggerItem
              key={story.id}
              className="bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300"
            >
              <div className="grid grid-cols-2 gap-2 p-2">
                <div className="relative aspect-square rounded-xl overflow-hidden">
                  <AppImage
                    src={story.beforeImage}
                    alt={story.beforeAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 px-3 py-1 bg-black/70 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                    Before
                  </div>
                </div>
                <div className="relative aspect-square rounded-xl overflow-hidden">
                  <AppImage
                    src={story.afterImage}
                    alt={story.afterAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 px-3 py-1 bg-accent backdrop-blur-sm rounded-full text-xs font-medium text-white">
                    After
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-headline font-semibold text-foreground">
                      {story.petName}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Adopted by {story.ownerName}
                    </p>
                  </div>
                  <Icon name="HeartIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
                </div>

                <p className="text-foreground leading-relaxed">{story.story}</p>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapPinIcon" size={16} variant="outline" />
                    <span>{story.location}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="CalendarIcon" size={16} variant="outline" />
                    <span>{story.adoptionDate}</span>
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;